import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Download, Package, Plus, Search, Upload, X } from 'lucide-react'

import { AdasBox, AdasTabs } from '@components/wrapper-components'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { ProductForm, ProductInfo, ProductList, ProductProfileSelector } from '@components/product'
import { SearchOrAddData } from '@components/common'
import { Logger } from '../../logger'
import {
  fetchPartsList,
  fetchServiceList,
  fetchCalibrationList,
  fetchLabourList,
  getCalibrationList,
  getLabourList,
  getServiceList,
  getPartsList
} from '../../api/api'
import { useUserStore } from '@caradasstore/UserStore'
import { ImportCSVComponent } from '@components/import-form'
import { uploadProductCSV, validateProductCSV } from '../../api/api'
import { GENERIC_MESSAGES } from 'constants'
import { downloadAsFile } from 'utils/utils'
import { PRODUCT_LIST_HEADERS, SERVICE_LIST_HEADERS, PARTS_LIST_HEADERS, LABOUR_LIST_HEADERS } from 'constants'

const IMPORT_CLIENT_STEPPER = [0, 1]

export const ProductsPage = () => {
  const [calibrationList, setCalibrationList] = useState([])
  const [serviceList, setServiceList] = useState([])
  const [partsList, setPartsList] = useState([])
  const [labourList, setLabourList] = useState([])
  const [currentTab, setCurrentTab] = React.useState('calibration')
  const [isProductInfo, setIsProductInfo] = useState(false)
  const [isAddProduct, setIsAddProduct] = useState(false)
  const [productData, setProductData] = useState({})
  const ccId = useCenterStore((store) => store.ccId)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const centerDetail = useCenterStore((store) => store.centerDetail)

  const [openImportForm, setOpenImportForm] = useState(false)
  const [activeStep, setActiveStep] = useState(IMPORT_CLIENT_STEPPER[0])
  const [file, setFile] = useState(null)
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState(null)
  const [csvValid, setCsvValid] = useState(true)
  const [CSVValidationResponse, setCSVValidationResponse] = useState(null)
  const [CSVUploadResponse, setCSVUploadResponse] = useState(null)
  const [selectedReferralId, setSelectedReferralId] = useState('')
  const [selectedProfileType, setSelectedProfileType] = useState('calibration')
  const [pagination, setPagination] = useState({
    calibration: { pageNo: 0, pageSize: 50, totalPages: 0, totalCount: 0, search: '' },
    service: { pageNo: 0, pageSize: 50, totalPages: 0, totalCount: 0, search: '' },
    part: { pageNo: 0, pageSize: 50, totalPages: 0, totalCount: 0, search: '' },
    labor: { pageNo: 0, pageSize: 50, totalPages: 0, totalCount: 0, search: '' }
  })

  const [searchInput, setSearchInput] = useState('')
  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])

  const allTabs = [
    { label: 'Calibrations', value: 'calibration' },
    { label: 'Services', value: 'service' },
    { label: 'Parts', value: 'part' },
    { label: 'Labor', value: 'labor' }
  ]

  // Auto Quote products only exist as Calibrations and Services — hide Parts and Labor there.
  const tabs = allTabs.filter((tab) => {
    if (selectedProfileType === 'auto_quote') {
      return tab.value === 'calibration' || tab.value === 'service'
    }
    return true
  })

  const user = useUserStore((store) => store.currentUser)
  const requiredFlags = [
    'is_sf_enabled',
    'is_pgw_enabled',
    'is_hanover_enabled',
    'is_geico_enabled',
    'is_allstate_enabled',
    'is_bearriver_enabled',
    'is_progressive_enabled',
    'is_erac_enabled',
    'is_nodak_enabled'
  ]

  const shouldShowProductProfile = requiredFlags.some((flag) => centerDetail?.[flag])

  const fetchAndSetList = async (listFetcher, setter, tabKey) => {
    try {
      setLoading(true)
      const currentParams = pagination[tabKey]
      const response = await listFetcher({
        pageNo: currentParams.pageNo,
        pageSize: currentParams.pageSize,
        status: currentParams.status,
        ccid: ccId,
        search: currentParams.search,
        ...(selectedReferralId && { belongsTo: 'REFERRAL', belongsToId: selectedReferralId }), // ✅ include if available
        ...(selectedProfileType === 'auto_quote' && { productClass: 'AUTO_QUOTE' }) // Auto Quote profile → auto-quote products only
      })

      const data = response.data
      setter(data.calibrations || data.services || data.parts || data.labours)

      setPagination((prev) => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          totalPages: data.totalPages || 0,
          totalCount: data.totalCount || 0
        }
      }))
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'fetchAndSetList', method: listFetcher.name, ccId } })
    } finally {
      setLoading(false)
    }
  }
  const fetchServices = () => fetchAndSetList(fetchServiceList, setServiceList, 'service')
  const fetchCalibrations = () => fetchAndSetList(fetchCalibrationList, setCalibrationList, 'calibration')
  const fetchParts = () => fetchAndSetList(fetchPartsList, setPartsList, 'part')
  const fetchLabour = () => fetchAndSetList(fetchLabourList, setLabourList, 'labor')

  const changeTab = (event, newValue) => {
    setCurrentTab(newValue)
    setSearchInput('') // Clear the search input
    setPagination((prev) => ({
      ...prev,
      [newValue]: {
        ...prev[newValue],
        search: '',
        pageNo: 0
      }
    }))
  }

  const selectProduct = (product) => {
    setProductData(product)
    setIsProductInfo(true)
  }

  const updateProductList = () => {
    const fetchProductList = {
      calibration: fetchCalibrations,
      service: fetchServices,
      part: fetchParts,
      labor: fetchLabour
    }

    if (fetchProductList[currentTab]) {
      fetchProductList[currentTab]() // Fetch data for the current tab
    }

    setIsAddProduct(false)
  }
  useEffect(() => {
    const performSearch = () => {
      setPagination((prev) => ({
        ...prev,
        [currentTab]: {
          ...prev[currentTab],
          search: searchInput,
          pageNo: 0 // Reset to first page on new search
        }
      }))
    }

    const timeoutId = setTimeout(performSearch, 1000)
    return () => clearTimeout(timeoutId)
  }, [searchInput, currentTab])

  const fetchData = async () => {
    try {
      setLoading(true)
      const fetchMap = {
        calibration: fetchCalibrations,
        service: fetchServices,
        part: fetchParts,
        labor: fetchLabour
      }

      if (fetchMap[currentTab]) {
        await fetchMap[currentTab]()
      }
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductsPage' } })
    } finally {
      setLoading(false)
    }
  }

  const fetchAllItems = async (fetcher, type) => {
    try {
      const data = await fetcher({
        ccid: ccId,
        ...(selectedReferralId && { belongsTo: 'REFERRAL', belongsToId: selectedReferralId }) // ✅ include if available
      })
      return data
    } catch (error) {
      Logger.error({ message: error, payload: { file: 'ProductsPage', method: `fetchAllItems-${type}` } })
      return []
    }
  }

  const downloadProducts = useCallback(
    async ({ template = false } = {}) => {
      setLoading(true)
      try {
        let allCalibrations = []
        let allServices = []
        let allParts = []
        let allLabours = []

        if (!template) {
          // Fetch all data without pagination
          allCalibrations = await fetchAllItems(getCalibrationList, 'calibration')
          allServices = await fetchAllItems(getServiceList, 'service')
          allParts = await fetchAllItems(getPartsList, 'part')
          allLabours = await fetchAllItems(getLabourList, 'labour')
        }

        let exportData = template
          ? []
          : [
              ...allCalibrations.map((product) => [
                `"${product.name}"`,
                'Calibration',
                `"${product.category?.name || ''}"`,
                `"${product.price}"`,
                `"${product.is_active}"`,
                product.description ? `"${product.description}"` : '',
                product.is_defaulted ? `"${product.is_defaulted}"` : 'false',
                '\n'
              ]),
              ...allServices.map((service) => [
                `"${service.name}"`,
                'Service',
                `"${service.category?.name || ''}"`,
                `"${service.price}"`,
                `"${service.is_active}"`,
                service.description ? `"${service.description}"` : '',
                service.is_defaulted ? `"${service.is_defaulted}"` : 'false',
                '\n'
              ]),
              ...allParts.map((part) => [
                `"${part.name}"`,
                'Part',
                `"${part.category?.name || ''}"`,
                `"${part.price}"`,
                `"${part.is_active}"`,
                part.description ? `"${part.description}"` : '',
                part.is_defaulted ? `"${part.is_defaulted}"` : 'false',
                '\n'
              ]),
              ...allLabours.map((labour) => [
                `"${labour.name}"`,
                'Labor',
                `"${labour.category?.name || ''}"`,
                `"${labour.price}"`,
                `"${labour.is_active}"`,
                labour.description ? `"${labour.description}"` : '',
                labour.is_defaulted ? `"${labour.is_defaulted}"` : 'false',
                '\n'
              ])
            ]

        exportData = [
          ['Product Name', 'Type', 'Category Name', 'Product Price', 'Status', 'Product Description', 'Default Product\n'],
          ...exportData
        ]

        const fileName = 'product_service_list.csv'
        const blob = new Blob(exportData, { type: 'text/plain' })
        downloadAsFile(blob, fileName)
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'ProductsPage', method: 'downloadProducts' } })
      } finally {
        setLoading(false)
      }
    },
    [ccId, selectedReferralId]
  )

  const handleValidateCSV = useCallback(async () => {
    if (file instanceof File) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      try {
        const response = await validateProductCSV({ file: formData, ccid: ccId })
        if (response.data.status === 'error') {
          setCsvValid(false)
        } else {
          setCsvValid(true)
        }
        setCSVValidationResponse(response.data.messages)
        setActiveStep((step) => step + 1)
      } catch (error) {
        if (error.status === 400) {
          setCSVValidationResponse(error.response.data.messages)
          setCsvValid(false)
          setActiveStep((step) => step + 1)
        } else {
          Logger.error({ message: error, payload: { file: 'ProductListPage', method: 'handleValidateCSV' } })
        }
      }
    }
  }, [file, validateProductCSV, setActiveStep, setCSVValidationResponse, setCsvValid, ccId])

  const handleCSVUpload = useCallback(async () => {
    if (file instanceof File) {
      const formData = new FormData()
      formData.append('file', file, file.name)
      const response = await uploadProductCSV({ file: formData, ccid: ccId })
      setCSVUploadResponse(response.data.messages)
      setActiveStep((step) => step + 1)
      fetchData()
    }
  }, [file, uploadProductCSV, setActiveStep, ccId])
  const handleClose = useCallback(() => {
    setFile(null)
    setFileUploadErrorMessage(null)
    setActiveStep(IMPORT_CLIENT_STEPPER[0])
    setCSVValidationResponse(null)
    setOpenImportForm(false)
    setCsvValid(true)
  }, [])
  const paginationHandler = useCallback(
    (event, page) => {
      setPagination((prev) => ({
        ...prev,
        [currentTab]: {
          ...prev[currentTab],
          pageNo: page - 1
        }
      }))
    },
    [currentTab]
  )

  const pageSizeHandler = useCallback(
    (event) => {
      setPagination((prev) => ({
        ...prev,
        [currentTab]: {
          ...prev[currentTab],
          pageSize: event.target.value,
          pageNo: 0
        }
      }))
    },
    [currentTab]
  )
  const hasFetchedInitially = useRef(false)
  const clearListForTab = (tab) => {
    switch (tab) {
      case 'calibration':
        setCalibrationList([])
        break
      case 'service':
        setServiceList([])
        break
      case 'part':
        setPartsList([])
        break
      case 'labor':
        setLabourList([])
        break
      default:
        break
    }
  }

  useEffect(() => {
    const isNationalWithoutReferral = selectedProfileType === 'national' && !selectedReferralId

    if (isNationalWithoutReferral) {
      clearListForTab(currentTab)
      return // 🛑 Stop further API calls
    }

    if (!hasFetchedInitially.current) {
      hasFetchedInitially.current = true
    } else {
      clearListForTab(currentTab)
    }

    const fetchMap = {
      calibration: fetchCalibrations,
      service: fetchServices,
      part: fetchParts,
      labor: fetchLabour
    }

    const fetchFn = fetchMap[currentTab]
    if (fetchFn) fetchFn()
  }, [
    currentTab,
    pagination[currentTab].pageNo,
    pagination[currentTab].pageSize,
    pagination[currentTab].search,
    ccId,
    selectedReferralId,
    selectedProfileType
  ])

  const canAddProduct =
    (user?.is_admin || user?.is_manager || user?.is_owner) &&
    selectedProfileType !== 'national' &&
    selectedProfileType !== 'auto_quote' &&
    !(selectedProfileType === 'national' && !selectedReferralId)

  const PROFILE_PILLS = [
    { value: 'calibration', label: 'Store' },
    { value: 'auto_quote', label: 'Auto Quote' },
    { value: 'national', label: 'National', disabled: !shouldShowProductProfile }
  ]

  const handleMobileProfileChange = (profile) => {
    setSelectedReferralId('')
    setSelectedProfileType(profile)
    setCurrentTab('calibration')
  }

  return (
    <div className='mx-auto max-w-[1400px] px-3 py-6 pb-[10px] min-[435px]:px-0'>
      {isProductInfo && (
        <ProductInfo
          open={isProductInfo}
          product={productData}
          productTypeName={currentTab}
          closeDrawer={() => setIsProductInfo(false)}
          onSaveSuccess={updateProductList}
          selectedProfileType={selectedProfileType}
        />
      )}
      {isAddProduct && (
        <ProductForm
          open={isAddProduct}
          productData={{ calibration_center_id: ccId }}
          productTypeName={currentTab}
          onSaveSuccess={updateProductList}
          close={() => setIsAddProduct(false)}
          selectedProfileType={selectedProfileType}
          selectedReferralId={selectedReferralId}
        />
      )}
      {/* Page heading (desktop only — mobile uses the global toolbar title) */}
      <AdasBox className='hidden min-[435px]:flex pt-6 md:pt-4 mb-6 flex-wrap items-center justify-between gap-3'>
        <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>Products</h1>
      </AdasBox>

      {/* Mobile inline search */}
      {searchOpen && (
        <div className='min-[435px]:hidden mb-3 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoFocus
            placeholder='Search products'
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </div>
      )}

      {/* Mobile profile pill row */}
      <div className='min-[435px]:hidden mb-4 flex items-center gap-2 overflow-x-auto'>
        {PROFILE_PILLS.map((opt) => {
          const active = selectedProfileType === opt.value
          return (
            <button
              key={opt.value}
              type='button'
              disabled={opt.disabled}
              onClick={() => handleMobileProfileChange(opt.value)}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-[13px] font-semibold transition-colors ${
                active ? 'border-[#1F6FEB] bg-[#E8EFFF] text-[#1F6FEB]' : 'border-[#D1D5D8] bg-white text-[#0A1628] hover:bg-[#F8FAFC]'
              } ${opt.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {opt.label}
            </button>
          )
        })}
      </div>

      {/* Profile selector — desktop renders pill cards; mobile only surfaces the
          National-Agreement warning + referral dropdown (gated inside the component). */}
      <div className='mt-2'>
        <ProductProfileSelector
          onCategorySelect={({ referralId, profile }) => {
            setSelectedReferralId(referralId)
            setSelectedProfileType(profile)
            setCurrentTab('calibration')
          }}
          shouldShowProductProfile={shouldShowProductProfile}
          selectedProfileType={selectedProfileType}
        />
      </div>

      {/* Tabs */}
      <div className='mt-2 min-[435px]:mt-4'>
        <AdasTabs tabs={tabs} value={currentTab} onChange={changeTab} aria-label='product listing tab set' />
      </div>

      {/* Search bar + Action buttons (desktop only) */}
      <AdasBox className='hidden min-[435px]:flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-5'>
        <AdasBox className='flex items-center flex-1 sm:flex-[0.6]'>
          <SearchOrAddData
            variant={
              selectedProfileType === 'auto_quote'
                ? 'withoutAddButton'
                : !(user?.is_admin || user?.is_manager || user?.is_owner) || selectedProfileType === 'national'
                  ? 'withAddButtonDisabled'
                  : 'withAddButton'
            }
            searchInput={searchInput}
            searchInputHandler={(e) => setSearchInput(e.target.value)}
            onAddClick={() => setIsAddProduct(true)}
          />
        </AdasBox>
        {user?.is_admin && (
          <AdasBox className='flex items-center gap-3 shrink-0'>
            {selectedProfileType !== 'national' && selectedProfileType !== 'auto_quote' && (
              <button
                onClick={() => setOpenImportForm(true)}
                className='inline-flex items-center gap-2 h-10 px-4 bg-[#0A1628] text-white text-[13px] font-semibold rounded-lg hover:bg-[#1F2D3D] transition-colors'
              >
                <Upload className='h-4 w-4' />
                Import CSV
              </button>
            )}
            {selectedProfileType !== 'auto_quote' && (selectedProfileType !== 'national' || selectedReferralId) && (
              <button
                onClick={downloadProducts}
                className='inline-flex items-center gap-2 h-10 px-4 bg-white border border-[#D1D5D8] rounded-lg text-[13px] font-semibold text-[#0A1628] hover:bg-[#F8FAFC] transition-colors'
              >
                <Download className='h-4 w-4' />
                Export CSV
              </button>
            )}
          </AdasBox>
        )}
      </AdasBox>

      {/* Table card */}
      {selectedProfileType === 'national' && !selectedReferralId ? (
        <div className='bg-card rounded-card border border-border shadow-level-1 overflow-hidden mt-5 flex items-center justify-center min-h-[200px] text-muted-foreground'>
          Please select a Referral Source to view National Agreement products.
        </div>
      ) : (
        <>
          {currentTab === 'calibration' && (
            <ProductList
              products={calibrationList}
              onSelectProduct={selectProduct}
              columns={PRODUCT_LIST_HEADERS}
              pageSizeHandler={pageSizeHandler}
              paginationHandler={paginationHandler}
              pageParams={pagination[currentTab]}
            />
          )}
          {currentTab === 'service' && (
            <ProductList
              products={serviceList}
              onSelectProduct={selectProduct}
              columns={SERVICE_LIST_HEADERS}
              pageSizeHandler={pageSizeHandler}
              paginationHandler={paginationHandler}
              pageParams={pagination[currentTab]}
            />
          )}
          {currentTab === 'part' && (
            <ProductList
              products={partsList}
              onSelectProduct={selectProduct}
              columns={PARTS_LIST_HEADERS}
              pageSizeHandler={pageSizeHandler}
              paginationHandler={paginationHandler}
              pageParams={pagination[currentTab]}
            />
          )}
          {currentTab === 'labor' && (
            <ProductList
              products={labourList}
              onSelectProduct={selectProduct}
              columns={LABOUR_LIST_HEADERS}
              pageSizeHandler={pageSizeHandler}
              paginationHandler={paginationHandler}
              pageParams={pagination[currentTab]}
            />
          )}
        </>
      )}

      {/* Floating Add Product button (mobile only) */}
      {canAddProduct && (
        <button
          type='button'
          onClick={() => setIsAddProduct(true)}
          className='min-[435px]:hidden fixed bottom-6 right-4 z-30 inline-flex items-center gap-2 rounded-full bg-[#0A1628] px-6 py-3.5 text-[14px] font-bold text-white shadow-lg transition-colors hover:bg-[#1F2D3D]'
        >
          <Plus className='h-5 w-5' />
          Add Product
        </button>
      )}

      <ImportCSVComponent
        open={openImportForm}
        title='Import Products'
        subtitle='Add multiple products at once from a CSV file'
        icon={Package}
        templateFileName='products_template.csv'
        uploadVariant='row'
        onClose={handleClose}
        downloadCSV={downloadProducts}
        handleValidateCSV={handleValidateCSV}
        handleCSVUpload={handleCSVUpload}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        csvValid={csvValid}
        CSVValidationResponse={CSVValidationResponse}
        file={file}
        IMPORT_CLIENT_STEPPER={IMPORT_CLIENT_STEPPER}
        GENERIC_MESSAGES={GENERIC_MESSAGES}
        CSVUploadResponse={CSVUploadResponse}
        setCsvValid={setCsvValid}
        setFile={setFile}
        fileUploadErrorMessage={fileUploadErrorMessage}
        setFileUploadErrorMessage={setFileUploadErrorMessage}
      />
    </div>
  )
}
